import type { ApiError, ApiResult } from '@ridemaadi/types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly error: ApiError,
  ) {
    super(error.message);
    this.name = 'ApiClientError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : null;
}

/** Thin fetch wrapper that unwraps the standard API envelope. */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const headers: Record<string, string> = {};
  if (body) headers['Content-Type'] = 'application/json';
  // Double-submit CSRF token for cookie-authenticated writes.
  if (method !== 'GET') {
    const csrf = readCookie('rm_csrf');
    if (csrf) headers['x-csrf-token'] = csrf;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: Object.keys(headers).length ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  // 204 No Content (logout, deletes) carries no envelope.
  if (res.status === 204) return undefined as T;

  const json = (await res.json().catch(() => null)) as ApiResult<T> | null;

  if (!res.ok || !json || json.ok === false) {
    const error: ApiError =
      json && json.ok === false
        ? json.error
        : { code: 'internal_error', message: `Request failed (${res.status})` };
    throw new ApiClientError(res.status, error);
  }

  return json.data;
}
